<template>
  <v-snackbar
    v-model="isVisible"
    :timeout="notification.timeout"
    location="bottom left"
    :color="notificationColor"
    elevation="0"
    class="global-notification"
    :style="{ '--border-color': borderColor }"
  >
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <v-icon color="#191919" size="24" class="mr-3">{{ notificationIcon }}</v-icon>
        <div style="color: #191919; font-size: 14px; line-height: 1.5">
          <strong v-if="notification.title">{{ notification.title }}&nbsp;</strong>
          <span>{{ notification.message }}</span>
        </div>
      </div>
      <v-btn icon variant="flat" @click="hideNotification" class="ml-4 notification-close-btn">
        <v-icon color="#191919" size="16">mdi-close-thick</v-icon>
      </v-btn>
    </div>
  </v-snackbar>
</template>

<script>
import { defineComponent, computed } from 'vue';
import { useUiStore } from '@/stores/ui';
import { Logger } from '@/mixins/logger';

// Default notification state to prevent errors with malformed data
const DEFAULT_NOTIFICATION = {
  show: false,
  type: 'success',
  title: '',
  message: '',
  timeout: 5000
};

export default defineComponent({
  name: 'GlobalNotification',

  setup() {
    const uiStore = useUiStore();

    // Safe accessor for notification with fallback to defaults
    const notification = computed(() => {
      try {
        const notif = uiStore.getNotification;
        // Ensure all required properties exist
        return {
          show: notif?.show ?? DEFAULT_NOTIFICATION.show,
          type: notif?.type ?? DEFAULT_NOTIFICATION.type,
          title: notif?.title ?? DEFAULT_NOTIFICATION.title,
          message: notif?.message ?? DEFAULT_NOTIFICATION.message,
          timeout: notif?.timeout ?? DEFAULT_NOTIFICATION.timeout
        };
      } catch (error) {
        Logger.methods.logError('GlobalNotification - Error accessing notification', error);
        return DEFAULT_NOTIFICATION;
      }
    });

    const notificationColor = computed(() => {
      const colors = {
        success: '#E9F5DB',
        error: '#ffebee',
        warning: '#fff8e1',
        info: '#e3f2fd'
      };
      return colors[notification.value.type] || colors.success;
    });

    const borderColor = computed(() => {
      const colors = {
        success: '#78BE20',
        error: '#c62828',
        warning: '#f57c00',
        info: '#1976d2'
      };
      return colors[notification.value.type] || colors.success;
    });

    const notificationIcon = computed(() => {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information'
      };
      return icons[notification.value.type] || icons.success;
    });

    const hideNotification = () => {
      try {
        uiStore.hideNotification();
      } catch (error) {
        Logger.methods.logError('GlobalNotification - Error hiding notification', error);
      }
    };

    // Writable computed for v-model on snackbar
    // This allows the snackbar timeout to properly close the notification
    const isVisible = computed({
      get: () => notification.value.show,
      set: (value) => {
        if (!value) {
          hideNotification();
        }
      }
    });

    return {
      notification,
      notificationColor,
      borderColor,
      notificationIcon,
      hideNotification,
      isVisible
    };
  }
});
</script>

<style scoped>
.global-notification :deep(.v-snackbar__wrapper) {
  min-width: 400px;
  max-width: 600px;
  border-radius: 0px;
  border: 1px solid var(--border-color);
}

.global-notification :deep(.v-snackbar__content) {
  padding: 12px 16px;
}

.notification-close-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  min-height: 24px !important;
  border: 1px solid #d0d0d0 !important;
  border-radius: 100px !important;
  padding: 0 !important;
  background-color: #ffffff !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

@media (max-width: 600px) {
  .global-notification :deep(.v-snackbar__wrapper) {
    min-width: 280px;
    max-width: 90vw;
  }
}
</style>
